import React, { useState } from 'react';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './Step1Company.module.css';

const AddressBlock = ({ prefix, title, badge }) => (
  <section className={css.section}>
    <h3 className={css.sectionTitle} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {title}
      {badge && (
        <span style={{ fontSize: 11, fontWeight: 600, background: '#dbeafe', color: '#1d4ed8', padding: '2px 10px', borderRadius: 99 }}>
          {badge}
        </span>
      )}
    </h3>

    <div className={css.fieldFull} style={{ marginBottom: 14 }}>
      <label className={css.label}>
        Street Address <span className={css.required}>*</span>
      </label>
      <Field name={`${prefix}Street`}>
        {({ input, meta }) => (
          <>
            <input
              {...input}
              className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
              placeholder="Street address, suite, unit…"
            />
            {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
          </>
        )}
      </Field>
    </div>

    <div className={css.flex}>
      <div className={css.field}>
        <label className={css.label}>
          City <span className={css.required}>*</span>
        </label>
        <Field name={`${prefix}City`}>
          {({ input, meta }) => (
            <>
              <input
                {...input}
                className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                placeholder="City"
              />
              {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
            </>
          )}
        </Field>
      </div>

      <div className={css.field}>
        <label className={css.label}>State / Province</label>
        <Field name={`${prefix}State`}>
          {({ input }) => (
            <input {...input} className={css.input} placeholder="State or province" />
          )}
        </Field>
      </div>
    </div>

    <div className={css.flex}>
      <div className={css.field}>
        <label className={css.label}>
          ZIP / Postal Code <span className={css.required}>*</span>
        </label>
        <Field name={`${prefix}PostalCode`}>
          {({ input, meta }) => (
            <>
              <input
                {...input}
                className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                placeholder="ZIP / Postal code"
              />
              {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
            </>
          )}
        </Field>
      </div>

      <div className={css.field}>
        <label className={css.label}>
          Country <span className={css.required}>*</span>
        </label>
        <Field name={`${prefix}Country`}>
          {({ input, meta }) => (
            <>
              <input
                {...input}
                className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                placeholder="Country"
              />
              {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
            </>
          )}
        </Field>
      </div>
    </div>
  </section>
);

const BuyerStep3Address = ({ nextStep, prevStep, initialValues }) => {
  const [sameAsShipping, setSameAsShipping] = useState(false);

  const validate = values => {
    const errors = {};
    if (!values.shippingStreet?.trim()) errors.shippingStreet = 'Street address is required';
    if (!values.shippingCity?.trim()) errors.shippingCity = 'City is required';
    if (!values.shippingPostalCode?.trim()) errors.shippingPostalCode = 'Postal code is required';
    if (!values.shippingCountry?.trim()) errors.shippingCountry = 'Country is required';
    if (!sameAsShipping) {
      if (!values.billingStreet?.trim()) errors.billingStreet = 'Street address is required';
      if (!values.billingCity?.trim()) errors.billingCity = 'City is required';
      if (!values.billingPostalCode?.trim()) errors.billingPostalCode = 'Postal code is required';
      if (!values.billingCountry?.trim()) errors.billingCountry = 'Country is required';
    }
    return errors;
  };

  const onSubmit = (values, form) => {
    const data = { ...values };
    if (sameAsShipping) {
      data.billingStreet = values.shippingStreet;
      data.billingCity = values.shippingCity;
      data.billingState = values.shippingState;
      data.billingPostalCode = values.shippingPostalCode;
      data.billingCountry = values.shippingCountry;
      data.sameAsShipping = true;
    }
    nextStep(data);
  };

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      initialValues={initialValues}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className={css.form}>

          <div className={css.header}>
            <span className={css.step}>Step 3 of 5</span>
            <h2 className={css.title}>Shipping & Billing Address</h2>
            <p className={css.description}>
              Enter your shipping and billing addresses for order and invoice processing.
            </p>
          </div>

          <hr className={css.divider} />

          <AddressBlock prefix="shipping" title="Shipping Address" badge="Required" />

          {/* Same as shipping toggle */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 16px',
            background: sameAsShipping ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : '#f8fafc',
            border: `1.5px solid ${sameAsShipping ? '#2563eb' : '#e2e8f0'}`,
            borderRadius: 12,
            cursor: 'pointer',
            marginBottom: 20,
            transition: 'all 0.2s',
          }}>
            <div style={{ width: 40, height: 22, borderRadius: 99, background: sameAsShipping ? '#2563eb' : '#cbd5e1', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
              <div style={{ position: 'absolute', top: 3, left: sameAsShipping ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
              <input type="checkbox" checked={sameAsShipping} onChange={e => setSameAsShipping(e.target.checked)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: sameAsShipping ? '#1d4ed8' : '#0f172a' }}>Billing address same as shipping</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Skip entering a separate billing address</div>
            </div>
          </label>

          {!sameAsShipping && (
            <AddressBlock prefix="billing" title="Billing Address" />
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, gap: 12 }}>
            <button type="button" onClick={prevStep} style={{ padding: '13px 24px', border: '1.5px solid #e2e8f0', borderRadius: 12, background: '#ffffff', color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              ← Previous
            </button>
            <button type="submit" style={{ padding: '13px 28px', border: 'none', borderRadius: 12, background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: '#ffffff', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(37,99,235,0.3)' }}>
              Next : Agreements →
            </button>
          </div>

        </form>
      )}
    />
  );
};

BuyerStep3Address.defaultProps = { initialValues: {} };
BuyerStep3Address.propTypes = {
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
};

export default BuyerStep3Address;
